import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { dsStyles, cssVars } from '../design-system';

/**
 * Toasts.
 *
 * The handoff calls for one on every action with a consequence, stacked and
 * auto-dismissing. Two details that are easy to get wrong and matter here:
 *
 *  - The stack is capped. A drag across a kanban board can fire a dozen actions in
 *    a few seconds; without a cap the toasts would cover the board they describe.
 *  - Each toast is announced once via a live region, and `bad` uses `alert` while
 *    the rest use `status`. Announcing a routine confirmation as an alert interrupts
 *    whatever a screen-reader user was reading.
 */

export type ToastTone = 'ok' | 'info' | 'warn' | 'bad';

interface Toast {
  readonly id: string;
  readonly tone: ToastTone;
  readonly message: string;
  readonly durationMs: number;
}

interface ToastApi {
  readonly show: (tone: ToastTone, message: string, durationMs?: number) => void;
  readonly ok: (message: string) => void;
  readonly info: (message: string) => void;
  readonly warn: (message: string) => void;
  readonly bad: (message: string) => void;
  readonly dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TONE_COLOR: Readonly<Record<ToastTone, string>> = {
  ok: 'var(--color-success)',
  info: 'var(--color-brand)',
  warn: 'var(--color-warning)',
  bad: 'var(--color-danger-bright)',
};

/** Above this the toasts obscure the UI they are describing. */
const MAX_VISIBLE = 4;

const DEFAULT_DURATION_MS = 4500;
/** A failure needs longer: the user has to read it and decide what to do. */
const BAD_DURATION_MS = 7000;

export const ToastProvider = ({ children }: { children: ReactNode }): ReactNode => {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const counter = useRef(0);

  const dismiss = useCallback((id: string): void => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (tone: ToastTone, message: string, durationMs?: number): void => {
      counter.current += 1;
      const id = `toast-${String(counter.current)}`;
      const duration = durationMs ?? (tone === 'bad' ? BAD_DURATION_MS : DEFAULT_DURATION_MS);

      setToasts((current) => {
        const next = [...current, { id, tone, message, durationMs: duration }];
        // Oldest fall off the top, so the newest — the one describing what just
        // happened — is always visible.
        return next.slice(-MAX_VISIBLE);
      });
    },
    [],
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      ok: (message) => {
        show('ok', message);
      },
      info: (message) => {
        show('info', message);
      },
      warn: (message) => {
        show('warn', message);
      },
      bad: (message) => {
        show('bad', message);
      },
      dismiss,
    }),
    [show, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className={dsStyles.toastStack}>
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const ToastItem = ({
  toast,
  onDismiss,
}: {
  toast: Toast;
  onDismiss: (id: string) => void;
}): ReactNode => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss(toast.id);
    }, toast.durationMs);
    return () => {
      clearTimeout(timer);
    };
  }, [toast.id, toast.durationMs, onDismiss]);

  return (
    <div
      className={dsStyles.toast}
      style={cssVars({ '--toast-color': TONE_COLOR[toast.tone] })}
      role={toast.tone === 'bad' ? 'alert' : 'status'}
      aria-live={toast.tone === 'bad' ? 'assertive' : 'polite'}
    >
      <span className={dsStyles.toastRail} aria-hidden="true" />
      {toast.message.includes('\n') ? (
        <>
          <span className={dsStyles.toastIcon} aria-hidden="true">
            ↺
          </span>
          <span className={dsStyles.toastText}>
            <strong>{toast.message.split('\n')[0]}</strong>
            <span>{toast.message.split('\n').slice(1).join('\n')}</span>
          </span>
        </>
      ) : (
        <span className={dsStyles.toastText}>{toast.message}</span>
      )}
      <button
        type="button"
        className={dsStyles.closeButton}
        onClick={() => {
          onDismiss(toast.id);
        }}
        aria-label="Descartar aviso"
        style={{ width: 32, height: 32, fontSize: 18 }}
      >
        ×
      </button>
      <span
        className={dsStyles.toastTimer}
        style={{ animationDuration: `${String(toast.durationMs)}ms` }}
        aria-hidden="true"
      />
    </div>
  );
};

export const useToast = (): ToastApi => {
  const api = useContext(ToastContext);
  if (api === null) throw new Error('useToast debe usarse dentro de <ToastProvider>.');
  return api;
};
